import {
  DynamicListOption,
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import {
  isFieldMappedValue,
  parseDynamicOption,
} from '@components/integrations/lists/dynamic-options-utils';
import { formatJsonTemplate } from '@components/text-editors/addons/json-template';
import { abbreviateDate } from '@utils/date';

import { TravellerJourneyActionDetailed } from 'api/types';

// A dynamic-list option is an object carrying both a value and a label (e.g. { label, value,
// shortId }). Both keys are required so file objects ({ src, name }) and other structured values
// are not mistaken for one.
export const isDynamicListOption = (value: unknown): value is Partial<DynamicListOption> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'value' in value &&
  'label' in value;

const optionText = (option: Partial<DynamicListOption>) => option.label ?? option.value ?? '';

// Show a select's label whatever its stored shape: a {value, label} option, its
// JSON-stringified form, a field-mapped `{{ template }}`, or a plain option id.
export const getSelectDisplayText = (
  value: unknown,
  field: IntegrationEndpointSelectVariable,
): string => {
  if (isFieldMappedValue(value)) {
    return typeof value.value === 'string' ? value.value : '';
  }
  const option = isDynamicListOption(value)
    ? value
    : typeof value === 'string'
      ? parseDynamicOption(value)
      : undefined;
  if (option) return optionText(option);
  const staticOption = field.options?.find((item) => item.value === value);
  return staticOption?.label ?? (value == null ? '' : String(value));
};

const resolveRequestValueText = (value: unknown, field: IntegrationEndpointVariable) => {
  if (field.type === 'date' && value) {
    return abbreviateDate(value as string | Date, 'en');
  }
  if (field.type === 'select') {
    return getSelectDisplayText(value, field);
  }
  if (
    field.type === 'array_of_strings' &&
    Array.isArray(value) &&
    (field.options?.length || value.some(isDynamicListOption))
  ) {
    return value
      .map((item) =>
        isDynamicListOption(item)
          ? optionText(item)
          : (field.options?.find((option) => option.value === item)?.label ?? item),
      )
      .join('\n');
  }
  if (field.type === 'boolean') {
    return String(value ?? false);
  }
  if (field.type === 'json' && typeof value === 'string') {
    return formatJsonTemplate(value);
  }
  return value;
};

export const getRequestValueText = (
  value: unknown,
  field: IntegrationEndpointVariable,
): string | undefined => {
  const text = resolveRequestValueText(value, field);
  // An object here would crash React. A field that is not a select has no options to read a
  // label from, so a list option shows the value it sent and reads like its plain siblings.
  if (typeof text === 'object' && text !== null) {
    return isDynamicListOption(text) ? (text.value ?? '') : JSON.stringify(text);
  }
  if (text == null) return undefined;
  return typeof text === 'string' ? text : String(text);
};

export const getRequestDataText = (
  action: TravellerJourneyActionDetailed<'integration'>,
  field: IntegrationEndpointVariable,
) => getRequestValueText(action.data.requestData[field.name], field);
